/**
 * TanWaitPanel (Phase 4, §7.3) — the decoupled S-pushTAN waiting screen. Polls
 * `useFintsChallengePoll` every 3s while the challenge is `pending`, shows the
 * live `challenge_text` from the bank, and renders a terminal state:
 *  - `completed` → a short success summary (accounts found / sync outcome);
 *    `onCompleted` fires exactly once so the caller can advance past this panel.
 *  - `failed`/`expired` → `ErrorState` with "Erneut versuchen" — the challenge
 *    itself is dead server-side, so retry means "let me try the operation
 *    again", not "poll again" (that's `onRetry`, supplied by the caller).
 *  - a transport/query error while polling → `ErrorState` with a refetch retry
 *    (the challenge may still be alive; only the poll request itself failed).
 *
 * Used both inline (FinTS setup) and inside a `Modal` (`ConnectionCard`'s sync
 * / re-auth flows) — no `window.location` redirects anywhere in this component.
 */
import { useEffect, useRef } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { ErrorState } from '../../../components/ui/ErrorState.tsx';
import { useFintsChallengePoll } from '../../../lib/hooks/useFints.ts';
import { SYNC_RUN_STATUS } from '../../../lib/labels.ts';
import type { FintsChallengePollOut } from '../../../lib/types.ts';

export interface TanWaitPanelProps {
  challengeId: string;
  /** Fired exactly once when the challenge reaches `status: 'completed'`. */
  onCompleted: (result: FintsChallengePollOut) => void;
  /** "Erneut versuchen" on a terminal `failed`/`expired` outcome. */
  onRetry: () => void;
}

export function TanWaitPanel({ challengeId, onCompleted, onRetry }: TanWaitPanelProps) {
  const query = useFintsChallengePoll(challengeId);
  const completedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (query.data?.status === 'completed' && completedForRef.current !== challengeId) {
      completedForRef.current = challengeId;
      onCompleted(query.data);
    }
  }, [challengeId, query.data, onCompleted]);

  if (query.isError) {
    return (
      <ErrorState
        title="TAN-Status konnte nicht abgerufen werden"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const data = query.data;

  if (data?.status === 'failed' || data?.status === 'expired') {
    return (
      <ErrorState
        title={
          data.status === 'expired' ? 'TAN-Anfrage abgelaufen' : 'TAN-Bestätigung fehlgeschlagen'
        }
        message={data.error ?? undefined}
        onRetry={onRetry}
      />
    );
  }

  if (data?.status === 'completed') {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-500 dark:text-emerald-400" aria-hidden />
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">TAN bestätigt.</p>
        {data.accounts ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {data.accounts.length} {data.accounts.length === 1 ? 'Konto' : 'Konten'} gefunden.
          </p>
        ) : null}
        {data.run ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Abgleich: {SYNC_RUN_STATUS[data.run.status]}.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      <div
        role="status"
        aria-live="polite"
        className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-sky-600 dark:border-slate-700 dark:border-t-sky-500"
      />
      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
        Bitte bestätigen Sie die Anfrage in Ihrer S-pushTAN-App.
      </p>
      {data?.challenge_text ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{data.challenge_text}</p>
      ) : null}
    </div>
  );
}
